<template>
  <main class="w-xl max-w-full flex flex-col gap-4 p-4">
    <div class="flex items-center">当前版本: {{ gitCommit }}</div>

    <div class="flex items-center gap-4">
      <span>显示 Debug 信息/按钮</span>
      <InputSwitch v-model="db.debug.showDebugLabel" />
    </div>

    <div class="flex items-center gap-4">
      <span>显示悬浮球</span>
      <InputSwitch v-model="db.debug.showDebugFloatingBall" />
    </div>

    <div class="flex items-center gap-4">
      <span>连接生产API</span>
      <InputSwitch
        :modelValue="db.debug.apiEnv == 'prod'"
        @update:modelValue="onApiEnvChange"
      />
    </div>

    <hr />

    <div class="flex items-center gap-4 flex-wrap">
      <span>一键登录</span>
      <InputText
        v-model="userIdOrEmail"
        placeholder="用户LID/邮箱"
        @keydown.enter="onDebugLogin"
      />

      <Button
        label="登录"
        :disabled="userIdOrEmail.length === 0"
        @click="onDebugLogin"
      />
      <Button
        v-if="showBackToSelfButton"
        :label="`回到自己（${db.debug.initialUserId}）`"
        scene="secondary"
        @click="onBackToInitalUser"
      />
      <Button
        label="访客登录"
        scene="secondary"
        @click="debugGuestLogin(router, route)"
      />
    </div>

    <div class="flex items-center gap-4">
      <span>跳过验证码发送，验证码统一为 666666</span>
      <InputSwitch v-model="db.debug.skip" />
    </div>

    <template v-if="showDataRelatedFunctions">
      <div class="flex items-center gap-4">
        <span>设置订单为 0 元</span>
        <InputSwitch v-model="db.debug.forceOrderPriceToZero" />
      </div>

      <div class="flex items-center gap-4">
        <span>跳过面包扣除（开启后闯关时不会消耗面包）</span>
        <InputSwitch v-model="db.debug.skipEnergy" />
      </div>

      <div class="flex items-center gap-4">
        <Calendar
          v-model="dates"
          selectionMode="multiple"
          :manualInput="false"
          :max-date="new Date()"
        />

        <Checkbox
          v-model="checked"
          :binary="true"
        />
        <span>跳转到签到页面</span>
        <Button
          label="签到"
          @click="onCheckIn"
        />
      </div>

      <div class="flex items-center gap-2 flex-wrap">
        指定日期：
        <Calendar
          :model-value="getDebugTsDay(debugDate)"
          selectionMode="single"
          placeholder="选择一个日期"
          :manualInput="false"
          showTime
          hourFormat="24"
          @update:model-value="onDebugTsChange"
        />

        <Button
          label="设置"
          @click="onDebugTsSet"
        ></Button>

        <Button
          label="清除"
          scene="secondary"
          @click="onDebugTsClear"
        ></Button>

        <span class="text-yellow">(注意：页面刷新后该日期将失效)</span>
      </div>
    </template>

    <hr />

    <div class="flex items-center gap-4">
      <span>卡面难度配置</span>
      <TextButton
        :label="_t('编辑配置')"
        @click="onCardDifficulyEdit"
      />
    </div>

    <div class="flex items-center gap-4">
      <span>队列参数配置</span>
      <TextButton
        :label="_t('编辑配置')"
        @click="onInsertQueueParamsEdit"
      />
    </div>

    <hr />

    <span>调试入口</span>
    <div class="flex gap-4 flex-wrap">
      <Button
        label="Debug Components"
        @click="onDebugComponents"
      />
      <Button
        label="编辑器测试"
        @click="onDebugEditor"
      />

      <Button
        label="学习单元历史"
        @click="() => onUnitHistoryShow()"
      />
      <Button
        label="上传图片"
        @click="() => onImageUpload()"
      />
      <Button
        label="关卡结算 动画"
        @click="onTestAnimation"
      ></Button>

      <Button
        label="领奖UI"
        @click="openRewardDialog"
      ></Button>

      <Button
        label="Button 样式"
        @click="openButtonDebug"
      ></Button>

      <Button
        label="卡片布局模版"
        @click="openCardLayoutDebug"
      ></Button>

      <Button
        label="Boss 关卡"
        @click="openBossDebug"
      ></Button>

      <Button
        label="排位赛相关 UI"
        @click="openRankUIDebug"
      ></Button>

      <Button
        v-if="_global.isInsideApp"
        label="App Debug"
        @click="showAppDebugPage"
      ></Button>
    </div>
    <div class="flex justify-end mt-4">
      <Button
        label="重置所有 debug 设置"
        scene="wrong"
        @click="onResetAllSettings"
      />
    </div>

    <hr />

    <div class="flex flex-col gap-2">
      <div class="font-medium">设备信息</div>
      <div class="flex items-center">
        设备像素比 (DPR): {{ devicePixelRatio }}
      </div>
      <div class="flex items-center">
        视口尺寸: 宽{{ viewportWidth }}px × 高{{ viewportHeight }}px
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { checkIn, loginByUserIdOrEmail } from '@/api/debug'
import db, { mergeDefaultDb } from '@/db'
import { useCommonStore } from '@/stores/common-store'
import { useRoute, useRouter } from 'vue-router'

import { computed, ref, onMounted, onUnmounted } from 'vue'

import Calendar from 'primevue/calendar'
import Checkbox from 'primevue/checkbox'
import { formatDate, isEmail } from '@/utils/misc'

import { RewardType } from '@/api/task'
import CardDifficultyConfig from './CardDifficultyConfig.vue'
import InsertQueueConfig from './InsertQueueConfig.vue'
import { afterLogin } from '@/shared/landing'
import { debugGuestLogin } from '@/debug'

const gitCommit = _global.gitCommit
const userIdOrEmail = ref('')
const router = useRouter()
const route = useRoute()

// 获取设备像素比
const devicePixelRatio = ref(window.devicePixelRatio || 1)

// 获取视口尺寸
const viewportWidth = ref(window.innerWidth)
const viewportHeight = ref(window.innerHeight)

// 监听窗口大小变化
function updateViewportSize() {
  viewportWidth.value = window.innerWidth
  viewportHeight.value = window.innerHeight
}

// 在组件挂载时添加事件监听器
onMounted(() => {
  window.addEventListener('resize', updateViewportSize)
})

// 在组件卸载时移除事件监听器
onUnmounted(() => {
  window.removeEventListener('resize', updateViewportSize)
})

// 会影响数据写入的 debug 功能，链接 prod env 时不开放
const showDataRelatedFunctions = computed(() => {
  return db.debug.apiEnv !== 'prod'
})

const showBackToSelfButton = computed(() => {
  if (db.debug.initialUserId == null) return false

  // 如果记录的是 email，则对比不是当前用户的 email
  if (isEmail(db.debug.initialUserId)) {
    return db.debug.initialUserId !== store.user?.email
    // 否则对比 id 就可以了
  } else {
    return db.debug.initialUserId !== store.user?.id
  }
})

function onApiEnvChange(useProd: boolean) {
  db.debug.apiEnv = useProd ? 'prod' : 'staging'
  // 切换环境后清除一下「自己」的身份标识
  db.debug.initialUserId = undefined
  _refreshApp()
}

const store = useCommonStore()

const dates = ref<Date[]>([])
// 是否跳转到签到页面
const checked = ref(false)

const emit = defineEmits<{
  done: []
}>()

function openRewardDialog() {
  _showRewards([{ type: RewardType.DIAMOND, value: 20 }])
}
function openButtonDebug() {
  emit('done')
  router.push({
    name: 'debug/button',
  })
}
function openCardLayoutDebug() {
  emit('done')
  router.push({
    name: 'debug/card-layout',
  })
}

function openBossDebug() {
  emit('done')
  router.push({
    name: 'debug/boss',
  })
}

function openRankUIDebug() {
  emit('done')
  router.push({
    name: 'debug/rank',
  })
}

function showAppDebugPage() {
  _bridge.showDebugPage()
}

function onCheckIn() {
  let list = dates.value.map(date => formatDate(date, 'YYYY-MM-DD'))

  if (list.length > 0) {
    checkIn(list).then(res => {
      store.checkInCtRes = res
      if (store.statsOverview) {
        store.statsOverview.accumulatedCheckInDays = res.accumulatedDays
        store.statsOverview.maxCheckInCtDays = res.maxContinuousDays
      }
      _message.success('签到成功')

      if (checked.value) {
        emit('done')

        router.push({
          name: 'debug/check-in',
        })
      }
    })
  } else {
    if (checked.value) {
      emit('done')
      router.push({
        name: 'debug/check-in',
      })
    }
  }
}

function login(userIdOrEmail: string, successMsg: string) {
  loginByUserIdOrEmail(userIdOrEmail).then(res => {
    if (res.code !== 0) {
      _message.error(res.message)
      return
    }

    store.login(res.data.token, res.data.user, true)
    afterLogin(router, route)
    store.clearStageUnit()
    _refreshApp()
    setTimeout(() => {
      _message.success(successMsg)
    })
  })
}

function onDebugLogin() {
  login(userIdOrEmail.value, '一键登录成功')
}

function onBackToInitalUser() {
  if (db.debug.initialUserId == null) return
  login(db.debug.initialUserId, '已回到自己账户')
}

function onCardDifficulyEdit() {
  _openDialog(CardDifficultyConfig, {
    title: '卡面难度',
    rootClass: 'w-[60vw] min-w-360px',
  })
}

function onInsertQueueParamsEdit() {
  _openDialog(InsertQueueConfig, {
    title: '参数配置',
    rootClass: 'w-[40vw] min-w-360px',
  })
}

function onTestAnimation() {
  emit('done')
  router.push({
    name: 'debug/lesson-end',
  })
}

function getDebugTsDay(debugDate: Date | null) {
  if (debugDate == null) return null

  return new Date(debugDate)
}

const debugDate = _store.debugTs
  ? ref(new Date(_store.debugTs))
  : ref<Date | null>(null)
function onDebugTsChange(val: string | Date | Date[] | string[] | undefined) {
  if (val && typeof val === 'string') {
    debugDate.value = new Date(val)
  } else if (val instanceof Date) {
    debugDate.value = val
  }
}

function onDebugTsSet() {
  if (debugDate.value) {
    _store.debugTs = debugDate.value.getTime()

    _refreshApp()
  }
}

function onDebugTsClear() {
  _store.debugTs = null

  _refreshApp()
}

function onDebugComponents() {
  emit('done')
  router.push({
    path: '/debug',
  })
}

function onDebugEditor() {
  emit('done')
  router.push({ name: 'debug/editor' })
}

async function onUnitHistoryShow() {
  const UnitHistory = (await import('@/components/UnitHistory.vue')).default

  _openDialog(UnitHistory, {
    title: '学习单元历史',
    rootClass: 'w-[80vw]',
  })
}

async function onImageUpload() {
  const ImageUpload = (await import('@/components/ImageUpload.vue')).default

  _openDialog(ImageUpload, {
    title: '图片上传',
  })
}

function onResetAllSettings() {
  // 使用mergeDefaultDb中的默认值重置debug设置
  const defaultDb = mergeDefaultDb({})
  Object.assign(db.debug, defaultDb.debug)

  // 清除调试时间戳
  _store.debugTs = null

  // 刷新应用以应用更改
  _refreshApp()
  _message.success('已重置所有调试设置')
}
</script>

<style scoped></style>
